import type { Board } from "../board";
import { DamageType } from "../damageType";
import { ALL_DIRECTIONS, directionDelta } from "../direction";
import { PRIORITY_LAST, type ProcessableThing } from "../interfaces";
import { PhysicalThing } from "../physicalThing";
import { PhysicalThingWithHp } from "../physicalThingWithHp";
import { randomChance, randomInt } from "../random";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";

const DISMISS_RATE_BASE = 0.02;
const DISMISS_RATE_PER_EMPTY = 0.02;
const SEALED_DECAY_FACTOR = 0.85;

/** Gas can occupy passable tiles plus impassable ones marked gas-permeable. */
export function isGasPassable(board: Board, xy: XY): boolean {
	if (!board.isInBounds(xy)) return false;
	return board.isPassable(xy) || board.getThingsAt(xy).some((t) => t.isGasPermeable());
}

export abstract class BaseGas extends PhysicalThing implements ProcessableThing {
	private compression: number;

	constructor(xy: XY, compression: number = 1) {
		super(xy, false);
		this.compression = compression;
	}

	// The concrete gas class. Drives both same-type detection (each gas only
	// sees its own kind when spreading) and the factory for new gas cells.
	protected abstract gasClass(): new (xy: XY, compression?: number) => BaseGas;

	// Which co-located things this gas harms. Defaults to everything with HP.
	protected shouldDamage(_thing: PhysicalThingWithHp): boolean {
		return true;
	}

	getCompression(): number {
		return this.compression;
	}

	setCompression(compression: number): void {
		this.compression = compression;
	}

	getZOrder(): number {
		return ZOrder.AIR;
	}

	processTurn(board: Board): void {
		const gasClass = this.gasClass();

		// Scan surroundings: count empty cells (spreadable, no existing gas)
		let emptyCells = 0;
		for (const dir of ALL_DIRECTIONS) {
			const scanXY = this.xy.add(directionDelta(dir));
			if (board.isInBounds(scanXY) && this.canSpreadGasTo(board, scanXY)) {
				if (board.getThingOfClass(gasClass, scanXY) === null) {
					emptyCells++;
				}
			}
		}

		// Dismiss state:
		if (this.compression <= 1) {
			if (randomChance(DISMISS_RATE_BASE + DISMISS_RATE_PER_EMPTY * emptyCells)) {
				board.removeAtEndOfTurn(this);
				return;
			}
		} else {
			// Spread state
			if (emptyCells > 0) {
				const newGasCells = randomInt(1, emptyCells);
				const newCompression = Math.floor(this.compression / (newGasCells + 1));

				let newGasCellsPut = 0;
				for (const dir of ALL_DIRECTIONS) {
					const scanXY = this.xy.add(directionDelta(dir));
					if (board.isInBounds(scanXY) && this.canSpreadGasTo(board, scanXY)) {
						if (board.getThingOfClass(gasClass, scanXY) === null) {
							board.addThing(new gasClass(scanXY, newCompression));
							newGasCellsPut++;
						}
					}
					if (newGasCellsPut >= newGasCells) {
						break;
					}
				}

				this.compression = newCompression;
			} else {
				// Sealed: pull toward neighbors' average and decay self.
				// Decay only here (not in spread) keeps outdoor dissipation unchanged
				// while ensuring sealed rooms eventually clear.
				let neighborCount = 0;
				let totalCompression = 0;

				for (const dir of ALL_DIRECTIONS) {
					const scanXY = this.xy.add(directionDelta(dir));
					if (board.isInBounds(scanXY)) {
						const scannedGas = board.getThingOfClass(gasClass, scanXY);
						if (scannedGas !== null) {
							neighborCount++;
							totalCompression += scannedGas.getCompression();
						}
					}
				}

				if (neighborCount > 0) {
					const avgCompression = totalCompression / neighborCount;
					this.setCompression(Math.floor(avgCompression * SEALED_DECAY_FACTOR));
				}
			}
		}

		// Deals damage to everything under the gas it is allowed to harm
		for (const thing of [...board.getThingsAt(this.xy)]) {
			if (thing.active && thing instanceof PhysicalThingWithHp && this.shouldDamage(thing)) {
				thing.applyDamage(DamageType.TOXIC_GAS, 5, this);
			}
		}
	}

	private canSpreadGasTo(board: Board, xy: XY): boolean {
		return isGasPassable(board, xy);
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), compression: this.compression };
	}
}
